import { Typography } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import { format, isBefore, isValid } from 'date-fns';
import delay from 'lodash/delay';
import isEqual from 'lodash/isEqual';
import uniq from 'lodash/uniq';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import useMemoCompare from '~/hooks/useMemoCompare';
import api from '~/services/api';
import { IDatesValidation } from '~/services/api/clientAllocation';
import {
  IAllocationClientsByGroup,
  IClientAllocationDetailed,
} from '~/services/api/clientAllocation/types';
import { IClientOptionByTeam } from '~/store/client/types';
import { useStoreActions, useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import { ITeamMemberMapped, transformFormToPayload } from '~/utils/clientAllocation';
import { normalizeDateString } from '~/utils/date/date';
import { extractErrorMessage } from '~/utils/error/error';
import extractFullName from '~/utils/text/extractFullName';
import useClientAllocationAvailability from './hooks/useClientAllocationAvailability';
import useFormStorage from './hooks/useFormStorage';
import useAddClientAllocationRequest from './hooks/useAddClientAllocation';
import extractArchivedClients from './helpers/extractArchivedClients';

import Button from '~/ui/components/common/Button';
import MembersFields from './MembersFields';
import NavigationConfirmModal from '~/ui/components/common/NavigationConfirmModal';
import AddAllocationFormToolbar from './components/AccordionMembers/AddAllocationFormToolbar';
import Input from '~/ui/components/inputs/Input';

import { IOption } from '~/ui/components/inputs/Select/types';
import { IDictionary } from '~/services/api/dictionaries/types';
import { NETWORK, VIEW_CLIENT_ALLOCATION, VIEW_MY_CLIENT_ALLOCATION } from '~/ui/constants/paths';
import { AllocationMode } from './constants/allocationMode';
import validate, { IFormDynamicValues } from './validate';
import { DRAFT_ALLOCATION_KEY } from '~/services/localStorage/allocations';

import styles from './ClientAllocationForm.module.scss';

interface IProps {
  teamMembers: ITeamMemberMapped[];
  clients: (IOption & { teams: IDictionary[] })[];
  locationGroups: IAllocationClientsByGroup[];
  actTeamId?: string;
  defaultValues?: any;
  clientAllocationDate?: string;
  clientAllocations?: IClientAllocationDetailed[];
  clinicId: string;
  clientsList?: IClientOptionByTeam[];
}

const defValues = {
  dateRange: '',
  includeWeekends: false,
};

const initialDatesValidation: IDatesValidation = {
  isValid: true,
  users: [],
};

const AddClientAllocationForm = ({
  actTeamId,
  teamMembers,
  clients,
  locationGroups,
  clinicId,
  clientAllocationDate,
  clientAllocations,
  clientsList,
  defaultValues = { ...defValues, teamId: actTeamId },
}: IProps): ReactElement => {
  const [isAdding, setIsAdding] = useState(false);
  const [withClearDraft, setWithClearDraft] = useState(false);
  const [datesValidation, setDatesValidation] = useState<IDatesValidation>(initialDatesValidation);
  const navigate = useNavigate();
  const { isActTeamMember, isGlobalUser, isActTeamLeader } = useRole();
  const { dateAvailability } = useStoreState(state => state.clientAllocation);

  const isDuplicatingLocationGroups = Object.keys(defaultValues).some(
    key => key.includes('groups') && defaultValues[key].length,
  );

  const isDisabledCreateButton =
    !!dateAvailability?.clients.length && dateAvailability?.clients.some(c => !c.canBeCreated);

  const addClientAllocationRequest = useAddClientAllocationRequest({
    isGlobalUser,
    isActTeamMember,
  });

  const initialAllocationMode = isDuplicatingLocationGroups
    ? AllocationMode.BY_LOCATION_GROUP
    : AllocationMode.BY_CLIENT;

  const form = useForm<IFormDynamicValues>({
    defaultValues: {
      ...defaultValues,
      allocationMode: initialAllocationMode,
      teamId: actTeamId,
    },
    resolver: validate,
  });
  const {
    watch,
    setError,
    clearErrors,
    formState: { isDirty, errors },
    handleSubmit,
    register,
    unregister,
  } = form;

  const { teamId, includeWeekends, dateRange: range, allocationMode, ...formValues } = watch();

  const { isRestored, clearDraft } = useFormStorage<IFormDynamicValues>(form, {
    key: DRAFT_ALLOCATION_KEY,
    conditionKey: 'teamId',
  });

  const handleChangeMode = () => {
    Object.keys(formValues).forEach(v => {
      if (v.startsWith('clients') || v.startsWith('groups') || v.startsWith('memo')) {
        unregister(v as keyof IFormDynamicValues);
      }
    });
    clearDraft();
  };

  const [from, to] = range || [];

  const isBeforeToFrom = useMemo(
    () => from && to && isBefore(new Date(to), new Date(from)),
    [from, to],
  );

  const assignedClients = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(formValues as { [key: string]: number[] | number }).filter(
          ([key]) =>
            key.startsWith('clients') &&
            Array.isArray((formValues as { [key: string]: number[] | number })[key]),
        ),
      ) as { [key: string]: number[] },
    [formValues],
  );

  const assignedTeams = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(formValues as { [key: string]: unknown }).filter(([key]) =>
          key.startsWith('teamId'),
        ),
      ) as { [key: string]: string },
    [formValues],
  );

  const selectedMembersIds = useMemo(
    () =>
      Object.keys(assignedClients)
        .filter(key => Array.isArray(assignedClients[key]) && assignedClients[key]?.length)
        .map(key => Number(key.split('_')[1])),
    [assignedClients],
  );

  const memoizedSelectedMembersIds = useMemoCompare(
    selectedMembersIds,
    (prev, next) => prev && isEqual(prev, next),
  );

  const { showError, showNotify } = useStoreActions(actions => actions.snackbar);

  useEffect(() => {
    if (isBeforeToFrom) {
      setError('dateRange', { message: 'Invalid date range. From date must be before To date' });
    } else {
      clearErrors('dateRange');
    }
  }, [
    setError,
    clearErrors,
    showError,
    isBeforeToFrom,
    allocationMode,
    memoizedSelectedMembersIds,
  ]);

  const activeMembers = useMemo(
    () => teamMembers.filter(teamMember => !teamMember.isDeleted),
    [teamMembers],
  );
  const onSubmit = async (values: any) => {
    try {
      setIsAdding(true);
      const payload = transformFormToPayload(values);

      const archivedClients = extractArchivedClients(clientAllocations);
      if (archivedClients.length) {
        const archivedClientsNames = uniq(
          archivedClients.map(client => extractFullName(client)),
        ).join(', ');
        const isMultipleArchived = archivedClients.length > 1;
        showError(
          `The archived client${isMultipleArchived ? 's' : ''} ${archivedClientsNames} ha${
            isMultipleArchived ? 've' : 's'
          } been removed from the created allocation`,
        );
      }

      if (!payload.allocations.length) {
        delay(
          () => showError('Please provide at least one client'),
          archivedClients.length ? 3000 : 0,
        );
        return;
      }

      await addClientAllocationRequest({
        clinicId,
        teamId: actTeamId,
        payload,
      });
      clearDraft();
      showNotify({ message: 'Client allocation successfully added' });
      navigate(NETWORK);
    } catch (e) {
      showError(extractErrorMessage(e));
      setIsAdding(false);
    }
  };

  const handleCancel = () => {
    setWithClearDraft(true);
    if (clientAllocationDate) {
      navigate(
        isGlobalUser
          ? VIEW_MY_CLIENT_ALLOCATION
          : VIEW_CLIENT_ALLOCATION.replace(':actTeamId', actTeamId).replace(
              ':clientAllocationDate',
              String(clientAllocationDate.split('T')[0]),
            ),
      );
    } else navigate(NETWORK);
    clearDraft();
  };

  // validate allocation dates for availability
  const validateClientAllocationRange = useCallback(async () => {
    try {
      if (!from || !to || isBeforeToFrom) {
        return;
      }
      const payload = {
        from: normalizeDateString(from),
        to: normalizeDateString(to, true),
        includeWeekends,
        userIds: memoizedSelectedMembersIds,
      };

      const validateOwnMethod = isGlobalUser
        ? api.clientAllocation.validateDateRangeGU
        : api.clientAllocation.validateOwnDateRange;

      const method =
        isActTeamMember || isGlobalUser
          ? validateOwnMethod
          : api.clientAllocation.validateDateRange;

      const validationResult = await method({ clinicId, teamId: actTeamId }, payload).then(
        r => r.data,
      );

      setDatesValidation(validationResult);
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  }, [
    actTeamId,
    clinicId,
    from,
    includeWeekends,
    isActTeamMember,
    isGlobalUser,
    memoizedSelectedMembersIds,
    showError,
    to,
    isBeforeToFrom,
  ]);

  useEffect(() => {
    if (!memoizedSelectedMembersIds.length) {
      clearErrors();
    }
    if (isBeforeToFrom) {
      setError('dateRange', { message: 'Invalid date range. From date must be before To date' });
    }
  }, [clearErrors, setError, memoizedSelectedMembersIds, isBeforeToFrom]);

  useEffect(() => {
    if (to && from && !isBeforeToFrom && memoizedSelectedMembersIds?.length) {
      clearErrors();
      if (memoizedSelectedMembersIds?.length) {
        validateClientAllocationRange();
      }
    } else if (!memoizedSelectedMembersIds.length) {
      setDatesValidation(initialDatesValidation);
    }
  }, [
    to,
    from,
    memoizedSelectedMembersIds.length,
    validateClientAllocationRange,
    clearErrors,
    actTeamId,
    clinicId,
    clientAllocations,
    isBeforeToFrom,
  ]);

  useClientAllocationAvailability({
    dateRange: {
      from: from && isValid(new Date(from)) ? format(new Date(from), 'yyyy-MM-dd') : '',
      to: to && isValid(new Date(to)) ? format(new Date(to), 'yyyy-MM-dd') : '',
    },
    clientsList: assignedClients,
    assignedTeams,
    clinicId,
    teamId: Number(actTeamId),
    enabled: !isBeforeToFrom,
  });

  const leavePageDescription =
    'Are you sure you want to cancel "Add Client Allocation"? All the changes will be discarded.';

  const clientsListByTeam = useMemo(
    () =>
      actTeamId
        ? clientsList?.filter(cl =>
            'teams' in cl
              ? cl.teams.some(t => t.id === Number(actTeamId))
              : cl.team?.id === Number(actTeamId),
          )
        : clientsList,
    [actTeamId, clientsList],
  );

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Paper>
          <section className={styles.paper}>
            <div className={styles.header}>
              <h4 className={styles.subTitle}>Client Allocation Details</h4>
            </div>

            <AddAllocationFormToolbar
              clientAllocationDate={clientAllocationDate}
              onAllocationModeChange={handleChangeMode}
            />
            <div className={styles.hidden}>
              <Input register={register} errors={errors} name="teamId" />
            </div>
            {!datesValidation?.isValid && (
              <Grid item sm={12} container direction="row" alignItems="center">
                <h5 className={styles.validationErrors}>
                  Note:{' '}
                  {`${datesValidation.users.length} team member${
                    datesValidation.users.length > 1 ? 's' : ''
                  } already ${
                    datesValidation.users.length > 1 ? 'have' : 'has'
                  } allocations within this
                  period!`}
                </h5>
              </Grid>
            )}

            <Typography variant="subtitle1" className={styles.assignedClientsTitle}>
              {allocationMode === AllocationMode.BY_CLIENT
                ? 'Assigned Clients per Team Member'
                : 'Location Groups per Team Member'}
            </Typography>

            {isRestored && (
              <MembersFields
                members={activeMembers}
                clients={clients}
                clientAllocationDate={clientAllocationDate}
                clientAllocations={clientAllocations}
                isGlobalUser={isGlobalUser}
                assignedClients={assignedClients}
                datesValidation={datesValidation}
                clinicId={clinicId}
                clientsList={clientsListByTeam}
                locationGroups={locationGroups}
                isDuplicatingLocationGroups={isDuplicatingLocationGroups}
                isActTeamLeader={isActTeamLeader}
                validateAvailability
              />
            )}
          </section>
        </Paper>
        <div className={styles.buttons}>
          <div className={styles.buttonWrapper}>
            <Button color="primary" variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
          <Button
            color="primary"
            variant="contained"
            type="submit"
            disabled={
              !from || !to || isDisabledCreateButton || !selectedMembersIds.length || isBeforeToFrom
            }
          >
            Add Client Allocation
          </Button>
        </div>
        <NavigationConfirmModal
          when={isDirty && !isAdding}
          description={leavePageDescription}
          beforeConfirm={withClearDraft ? clearDraft : undefined}
        />
      </form>
    </FormProvider>
  );
};

export default AddClientAllocationForm;
